<template>
  <v-container fluid>
    <h2 class="mb-5">動作ログ</h2>

    <v-card max-width="800" class="mx-auto">
      <v-card-text class="px-6">
        <v-form @submit.prevent ref="form">
          <v-row no-gutters>

            <v-col :cols="$vuetify.display.mobile ? 12 : 2" class="d-flex align-center">
              <v-label>モジュール</v-label>
            </v-col>
            <v-col :cols="$vuetify.display.mobile ? 12 : 5">
              <v-select variant="outlined" density="compact" hide-details class="my-4"
                v-model:model-value="moduleID" :items="browsableModules"
                :rules="[ v => (v !== '') || '必須' ]"
                :readonly="isLoadingLogs"></v-select>
            </v-col>
            <v-col :cols="$vuetify.display.mobile ? 0 : 5"></v-col>

            <v-col :cols="$vuetify.display.mobile ? 12 : 2" class="d-flex align-center">
              <v-label>日付</v-label>
            </v-col>
            <v-col :cols="$vuetify.display.mobile ? 12 : 5">
              <vue-date-picker locale="ja" week-start="0" :enable-time-picker="false" auto-apply
                :teleport="true" class="my-3"
                v-model="date" :format="formatDate" :clearable="false"
                :readonly="isLoadingLogs"></vue-date-picker>
            </v-col>

            <v-col cols="12" class="mt-4" v-if="logs && logs.length === 0">
              <v-alert variant="tonal" color="error">
                指定された条件のログはありません
              </v-alert>
            </v-col>

          </v-row>
        </v-form>
      </v-card-text>

      <v-card-actions class="pa-3 justify-end">
        <v-btn variant="flat" color="primary" prepend-icon="mdi-magnify" class="px-3"
          v-on:click="fetchLogs" :loading="isLoadingLogs">
          検索
        </v-btn>
      </v-card-actions>
    </v-card>

    <div style="max-width: 800px;" class="mx-auto" v-if="logs && logs.length > 0">

      <v-pagination :length="Math.ceil(logs.length / pageMaxLogCount)" class="ma-7" v-model="page"></v-pagination>

      <v-card>
        <v-card-text class="pa-3">

          <v-divider></v-divider>

          <div v-for="log in logs.slice((pageMaxLogCount * (page - 1)), pageMaxLogCount * page)" v-bind:key="(log.timestamp + '#' + log.payload.id)">
            <div class="py-3 px-2">
              <div class="d-flex justify-space-between">
                <span>
                  {{ log.timestamp }}
                </span>
                <span class="text-grey" v-bind:class="{ 'v-hidden': !('id' in log.payload) }">
                  {{ '#' + log.payload.id }}
                </span>
              </div>

              <div class="mt-2 mx-1">
                {{ convertToFriendlyText(log.payload) }}
              </div>
            </div>

            <v-divider></v-divider>
          </div>

        </v-card-text>
      </v-card>

      <v-pagination :length="Math.ceil(logs.length / pageMaxLogCount)" class="ma-7" v-model="page"></v-pagination>

    </div>

  </v-container>
</template>

<script>
import { ref, onMounted } from 'vue';

import VueDatePicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';

import axios from 'axios';

import config from '@/config.js';

export default {
  name: 'ActionLogView',
  components: {
    VueDatePicker
  },

  setup() {

    const form = ref();

    const moduleID = ref('');
    const browsableModules = ref([
      { title: '未選択', value: '' }
    ]);

    const date = ref(new Date());

    const isLoadingLogs = ref(false);

    const page = ref(1);
    const pageMaxLogCount = ref(15);
    const logs = ref(null);

    const formatDate = (date) => {
      const year = date.getFullYear().toString();
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const day = date.getDate().toString().padStart(2, '0');

      return year + '-' + month + '-' + day;
    };

    onMounted(() => {
      fetchModuleList();
    });

    const fetchModuleList = () => {

      axios.get(
        config.API_URL +
        `/users/me`,
        { headers: { Authorization: 'Bearer ' + window.sessionStorage.getItem("access-token") } }
      ).then((response) => {

        response.data.browsableModules.forEach((moduleID) => {
          browsableModules.value.push({
            title: moduleID,
            value: moduleID
          });
        });

      }).catch((/* error */) => {
        // TBD リフレッシュトークンの利用を検討
        window.sessionStorage.clear();
        window.sessionStorage.setItem('access-token-expired', 'true');

        window.location.reload();
      });

    };

    const fetchLogs = async () => {

      const { valid } = await form.value.validate();
      if (!valid) {
        return;
      }

      isLoadingLogs.value = true;
      page.value = 1;
      logs.value = null;


      axios.get(
        config.API_URL +
        `/modules/${ moduleID.value }/logs?date=${ formatDate(date.value) }`,
        { headers: { Authorization: 'Bearer ' + window.sessionStorage.getItem("access-token") } }
      ).then((response) => {

        logs.value = response.data.logs;
        isLoadingLogs.value = false;

      }).catch((/* error */) => {
        // TBD リフレッシュトークンの利用を検討
        window.sessionStorage.clear();
        window.sessionStorage.setItem('access-token-expired', 'true');

        window.location.reload();
      });

    };

    const convertToFriendlyText = (payload) => {

      let text = '----------';

      try {

        if ('cmd' in payload) {
          switch (payload.cmd) {

            case 'output':

              text = `出力指示を送信`;

              if ('do1' in payload) {
                text += `（デジタル出力1を`;

                if ('duration' in payload.do1) {
                  if (payload.do1.duration % 10 === 0) {
                    text += `${ (payload.do1.duration / 10).toString() }秒間`;
                  } else {
                    text += `${ payload.do1.duration.toString() }00ミリ秒間`;
                  }
                }

                text += `${ (payload.do1.val === 1) ? 'ON' : 'OFF' }）`;
              }

              if ('do2' in payload) {
                text += `（デジタル出力2を`;

                if ('duration' in payload.do2) {
                  if (payload.do2.duration % 10 === 0) {
                    text += `${ (payload.do2.duration / 10).toString() }秒間`;
                  } else {
                    text += `${ payload.do2.duration.toString() }00ミリ秒間`;
                  }
                }

                text += `${ (payload.do2.val === 1) ? 'ON' : 'OFF' }）`;
              }

              break;

            case 'setSchedule':

              text = `出力予約指示を送信`;

              text += `（デジタル出力${ payload.target.substring(2) }, ${ payload.time }に`;

              if ('duration' in payload.action) {
                if (payload.action.duration % 10 === 0) {
                  text += `${ (payload.action.duration / 10).toString() }秒間`;
                } else {
                  text += `${ payload.action.duration.toString() }00ミリ秒間`;
                }
              }

              text += `${ (payload.action.val === 1) ? 'ON' : 'OFF' }）`;

              break;

            case 'cancelSchedule':

              text = `出力予約キャンセル指示を送信`;

              text += `（デジタル出力${ payload.target.substring(2) }）`;

              break;

            case 'setAlarm':

              text = `アラーム出力の設定指示を送信`;

              text += `（デジタル出力${ payload.target.substring(2) }, `;

              text += `判定チャンネル = ${ payload.input.startsWith('a') ? 'アナログ' : 'デジタル入力' }${ payload.input.substring(1) }）`;

              break;

            case 'deleteAlarm':

              text = `アラーム出力設定の解除指示を送信`;

              text += `（デジタル出力${ payload.target.substring(2) }）`;

              break;

            case 'setReboot':

              text = `定期再起動の設定指示を送信`;

              text += `（毎日 ${ payload.rebootAt }）`;

              break;

            case 'unsetReboot':

              text = `定期再起動の設定解除指示を送信`;

              break;

            case 'reboot':

              text = `再起動指示を送信`;

              break;

          }
        } else {
          switch (payload.type) {

            case 'boot':

              text = `モジュール起動`;

              text += `（デジタル出力1 = ${ (payload.do1 === 1) ? 'ON' : 'OFF' }, デジタル出力2 = ${ (payload.do2 === 1) ? 'ON' : 'OFF' }）`;

              break;

            case 'notifyAction':

              switch (payload.trigger) {

                case 'manual':

                  text = `出力を実行`;

                  break;

                case 'schedule':

                  text = `出力予約を実行`;

                  break;

                case 'alarm':

                  text = `${ payload.alarm.inputChannel.startsWith('a') ? 'アナログ' : 'デジタル入力' }${ payload.alarm.inputChannel.substring(1) }の計測値が`;

                  if (payload.alarm.condition === 0) {

                    text += `正常範囲に復帰したため`;

                  } else  {

                    if (payload.alarm.condition > 0) {
                      text += `上限値${ payload.alarm.condition.toString() }段階目以上となったため`;
                    } else {
                      text += `下限値${ Math.abs(payload.alarm.condition).toString() }段階目以下となったため`;
                    }

                  }

                  text += `、アラーム設定による出力を実行`;

                  break;

              }

              for (let i = 0; i < payload.actions.length; i++) {
                text += `（デジタル出力${ payload.actions[i].target.substring(2) }`;

                if ('duration' in payload.actions[i]) {
                  if (payload.actions[i].duration.amount % 10 === 0) {
                    text += `の${ (payload.actions[i].duration.amount / 10).toString() }秒間`;
                  } else {
                    text += `の${ payload.actions[i].duration.amount.toString() }00ミリ秒間`;
                  }

                  text += `${ (payload.actions[i].outputVal === 1) ? 'ON' : 'OFF' }`;
                  text += `を${ (payload.actions[i].duration.timing === 'start') ? '開始' : '終了' }）`;
                } else {
                  text += `を${ (payload.actions[i].outputVal === 1) ? 'ON' : 'OFF' }）`;
                }
              }

              break;

            case 'ackCommand':

              text = `指示を受け付け`;

              break;

          }
        }

      } catch {

        text = '----------';

        console.error(JSON.stringify(payload));
      }

      return text;

    };

    return {
      form,

      moduleID,
      browsableModules,

      date,

      isLoadingLogs,

      page,
      pageMaxLogCount,
      logs,

      formatDate,

      fetchLogs,

      convertToFriendlyText
    }

  }
}
</script>
